import { usePaymentLanguage } from '../../lib/paymentLanguage';
import React, { useState, useRef, useEffect } from 'react';
import { Input } from './input';
import { Label } from './label';
import { BANKS } from '../../data/banks';
import { Building2, ChevronDown, X } from 'lucide-react';

interface BankAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    label?: string;
    id?: string;
}

export const BankAutocomplete: React.FC<BankAutocompleteProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    error,
    label,
    id
}) => {
    const { t } = usePaymentLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [filteredBanks, setFilteredBanks] = useState<string[]>([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [selectedBank, setSelectedBank] = useState<string | null>(value || null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const searchQuery = selectedBank === value ? '' : value;

    // Фильтрация банков по введенному тексту
    useEffect(() => {
        if (searchQuery && searchQuery.length > 0) {
            const filtered = BANKS.filter(bank =>
                bank.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredBanks(filtered);
        } else {
            setFilteredBanks([...BANKS]); // Показываем все банки если поле пустое
        }
        setHighlightedIndex(-1);
    }, [searchQuery]);

    useEffect(() => {
        if (!value) {
            setSelectedBank(null);
        }
    }, [value]);

    // Автоскролл к выделенному элементу
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
            if (highlightedElement) {
                highlightedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [highlightedIndex]);

    // Обработка ввода
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedBank(null);
        onChange(e.target.value);
        setIsOpen(true);
    };

    // Обработка фокуса
    const handleInputFocus = () => {
        setIsOpen(true);
    };

    // Обработка клика по стрелочке
    const handleDropdownToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            inputRef.current?.focus();
        }
    };

    // Очистка поля
    const handleClear = () => {
        setSelectedBank(null);
        onChange('');
        setIsOpen(true);
        inputRef.current?.focus();
    };

    // Обработка потери фокуса
    const handleInputBlur = () => {
        // Небольшая задержка чтобы клик по элементу списка успел сработать
        setTimeout(() => setIsOpen(false), 150);
    };

    // Выбор банка из списка
    const handleBankSelect = (bank: string) => {
        setSelectedBank(bank);
        onChange(bank);
        setIsOpen(false);
        inputRef.current?.focus();
    };

    // Обработка клавиш
    const handleKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else if (filteredBanks.length > 0) {
                    setHighlightedIndex(prev =>
                        prev < filteredBanks.length - 1 ? prev + 1 : 0
                    );
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (isOpen && filteredBanks.length > 0) {
                    setHighlightedIndex(prev =>
                        prev > 0 ? prev - 1 : filteredBanks.length - 1
                    );
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredBanks.length) {
                    handleBankSelect(filteredBanks[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    return (
        <div className="relative space-y-2">
            {label && <Label htmlFor={id}>{label}</Label>}
            <div className="relative">
                <Input
                    ref={inputRef}
                    id={id}
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder ?? t("Начните вводить название банка...")}
                    disabled={disabled}
                    autoComplete="off"
                    className="pr-16" // Увеличиваем padding для двух кнопок
                />

                {/* Кнопки справа */}
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                    {/* Крестик для очистки */}
                    {value && !disabled && (
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded"
                            tabIndex={-1}
                            title={t("Очистить")}
                        >
                            <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300" />
                        </button>
                    )}

                    {/* Стрелочка dropdown */}
                    <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleDropdownToggle}
                        disabled={disabled}
                        className={`p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                            }`}
                        tabIndex={-1}
                        title={t("Открыть список")}
                    >
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''
                            }`} />
                    </button>
                </div>

                {/* Выпадающий список */}
                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-md shadow-lg">
                        {/* Заголовок со счетчиком */}
                        {filteredBanks.length > 0 && (
                            <div className="px-3 py-1 text-xs text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 rounded-t-md">
                                {searchQuery ? t("Найдено: {count}", { count: filteredBanks.length }) : t("Всего банков: {count}", { count: filteredBanks.length })}
                            </div>
                        )}

                        <ul
                            ref={listRef}
                            className="max-h-56 overflow-auto"
                        >
                            {filteredBanks.length === 0 ? (
                                <li className="px-3 py-2 text-sm text-gray-500 dark:text-slate-400 text-center">
                                    {t("Банки не найдены")}
                                </li>
                            ) : (
                                filteredBanks.map((bank, index) => {
                                    // Подсвечиваем совпадающий текст
                                    const highlightText = (text: string, query: string) => {
                                        if (!query) return text;
                                        const parts = text.split(new RegExp(`(${query})`, 'gi'));
                                        return parts.map((part, i) =>
                                            part.toLowerCase() === query.toLowerCase() ?
                                                <mark key={i} className="bg-yellow-200 dark:bg-yellow-950">{part}</mark> : part
                                        );
                                    };

                                    return (
                                        <li
                                            key={bank}
                                            className={`px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 dark:hover:bg-blue-950 ${index === highlightedIndex ? 'bg-blue-100 dark:bg-blue-950' : ''
                                                } flex items-center gap-2`}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleBankSelect(bank)}
                                        >
                                            <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                            <span className="flex-1">{highlightText(bank, searchQuery)}</span>
                                        </li>
                                    );
                                }))}
                        </ul>
                    </div>
                )}
            </div>

            {error && (
                <p className="text-sm text-red-500">{error}</p>
            )}
        </div>
    );
};
